import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeEvery, takeLatest, getContext, put, call} from 'redux-saga/effects'
import Api, {UnauthorizedError} from 'Api'

export type BulkUploadBatchStatus = 'success' | 'partial' | 'failed' | 'unknown'

// Plain JSON shapes as returned by the API — not the full model classes, which add
// behavior (validation, formatting) this read-only audit view doesn't need.
export type BulkUploadBatchUser = {id?: string; email: string}
export type BulkUploadBatchPatient = {id: string; firstName: string; lastName: string}
export type BulkUploadBatchDevice = {id: string; udid: string; name?: string}

export type BulkUploadBatchListItem = {
  id: string
  fileName?: string
  rowCount: number
  // null on batches persisted before outcome tracking existed — see status: 'unknown'.
  successCount: number | null
  failedCount: number | null
  rolledBack: boolean
  user?: BulkUploadBatchUser
  createdAt: string
  status: BulkUploadBatchStatus
  patientsCreated: number
  devicesAssigned: number
  patientsUpdated: number
}

export type BulkUploadBatchEvent =
  | {
      id: string
      rowNumber: number | null
      failed: false
      actionType: number
      patient?: BulkUploadBatchPatient
      device?: BulkUploadBatchDevice
    }
  | {
      id: string
      rowNumber: number | null
      failed: true
      message: string
      patientName?: string | null
      deviceSerial?: string | null
    }

export type BulkUploadBatchDetail = BulkUploadBatchListItem & {
  events: BulkUploadBatchEvent[]
}

export type BulkUploadBatchesState = {
  // Split from a single loading/error pair: getList and getDetail run independently (a user can
  // browse the list while a previous detail fetch is still settling, or vice versa), and sharing
  // one pair let a failure/in-flight state from one operation get misattributed to the other view.
  listLoading: boolean
  listError: string | undefined
  detailLoading: boolean
  detailError: string | undefined
  batches: BulkUploadBatchListItem[]
  selectedBatch: BulkUploadBatchDetail | undefined
}

export const initialState: BulkUploadBatchesState = {
  listLoading: false,
  listError: undefined,
  detailLoading: false,
  detailError: undefined,
  batches: [],
  selectedBatch: undefined,
}

export const actions = {
  getList: createAction('bulkUploadBatches/getList'),
  setList: createAction<{batches: BulkUploadBatchListItem[]}>('bulkUploadBatches/setList'),
  getDetail: createAction<{id: string}>('bulkUploadBatches/getDetail'),
  setDetail: createAction<{batch: BulkUploadBatchDetail}>('bulkUploadBatches/setDetail'),
  clearDetail: createAction('bulkUploadBatches/clearDetail'),
  setListLoading: createAction<{loading: boolean}>('bulkUploadBatches/setListLoading'),
  setListError: createAction<{error?: string}>('bulkUploadBatches/setListError'),
  setDetailLoading: createAction<{loading: boolean}>('bulkUploadBatches/setDetailLoading'),
  setDetailError: createAction<{error?: string}>('bulkUploadBatches/setDetailError'),
}

export const reducer = createReducer(initialState, builder => {
  builder
    .addCase(actions.setList, (state, action) => {
      state.batches = action.payload.batches
      state.listLoading = false
      state.listError = undefined
    })
    .addCase(actions.setDetail, (state, action) => {
      state.selectedBatch = action.payload.batch
      state.detailLoading = false
      state.detailError = undefined
    })
    .addCase(actions.clearDetail, state => {
      state.selectedBatch = undefined
      state.detailError = undefined
    })
    .addCase(actions.setListLoading, (state, action) => {
      state.listLoading = action.payload.loading
    })
    .addCase(actions.setListError, (state, action) => {
      state.listError = action.payload.error
      state.listLoading = false
    })
    .addCase(actions.setDetailLoading, (state, action) => {
      state.detailLoading = action.payload.loading
    })
    .addCase(actions.setDetailError, (state, action) => {
      state.detailError = action.payload.error
      state.detailLoading = false
    })
})

// The backend hard-caps a single page at 200 batches and has no unpaginated "fetch all"
// mode, so a single request can silently miss older batches once the org has more than
// one page's worth — this audit view needs to see all of them, so we page through.
const BULK_UPLOAD_BATCHES_PAGE_LIMIT = 200
// Safety valve in case the backend ever reports a `total` the paged fetches can't reach.
const BULK_UPLOAD_BATCHES_MAX_PAGES = 50

const getListSaga = function*() {
  yield put(actions.setListLoading({loading: true}))
  const api: Api = yield getContext('api')
  try {
    const batches: BulkUploadBatchListItem[] = []
    let page = 1
    let total = Infinity
    while (batches.length < total && page <= BULK_UPLOAD_BATCHES_MAX_PAGES) {
      const response: {batches: BulkUploadBatchListItem[]; total: number} = yield call(
        api.getBulkUploadBatches,
        page,
        BULK_UPLOAD_BATCHES_PAGE_LIMIT,
      )
      if (response.batches.length === 0) break
      batches.push(...response.batches)
      total = response.total
      page += 1
    }
    yield put(actions.setList({batches}))
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) throw error
    yield put(
      actions.setListError({error: error?.message ?? 'Error loading bulk upload batches'}),
    )
  }
}

const getDetailSaga = function*(action: ReturnType<typeof actions.getDetail>) {
  yield put(actions.setDetailLoading({loading: true}))
  const api: Api = yield getContext('api')
  try {
    const batch: BulkUploadBatchDetail = yield call(api.getBulkUploadBatch, action.payload.id)
    yield put(actions.setDetail({batch}))
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) throw error
    yield put(actions.setDetailError({error: error?.message ?? 'Error loading bulk upload batch'}))
  }
}

export const saga = function*() {
  yield takeEvery(actions.getList, getListSaga)
  // takeLatest cancels a still-in-flight getDetail when a newer one is dispatched, so a
  // stale response from a previously-viewed batch can never overwrite the current selection.
  yield takeLatest(actions.getDetail, getDetailSaga)
}
